import type { RowData } from '@tanstack/react-table';
import type { ReactNode } from 'react';

export interface DataColumn<T> {
  id: string;
  header: string;
  /** Plain value for sorting and CSV export. Columns without one are not sortable. */
  accessor?: (row: T) => string | number | boolean | null | undefined;
  /** Rendered cell; defaults to the accessor's value. */
  cell?: (row: T) => ReactNode;
  sortable?: boolean;
  /** CSS width (e.g. '160px', '12rem'). Columns without a width share the rest. */
  width?: string;
  align?: 'start' | 'end';
  /** The card title in card-list mode (exactly one column should set this). */
  primary?: boolean;
  /** Shown under the title in card-list mode. */
  secondary?: boolean;
  /** Users may hide it from the column chooser (default true; the primary column cannot). */
  hideable?: boolean;
  defaultHidden?: boolean;
  /** Include in CSV export (default: when it has an accessor). */
  csv?: boolean;
}

/** "name" ascending, "-name" descending. */
export type SortParam = string | undefined;

export function parseSort(sort: SortParam): { id: string; desc: boolean }[] {
  if (!sort) return [];
  return sort.startsWith('-') ? [{ id: sort.slice(1), desc: true }] : [{ id: sort, desc: false }];
}

export function formatSort(state: { id: string; desc: boolean }[]): SortParam {
  const first = state[0];
  if (!first) return undefined;
  return first.desc ? `-${first.id}` : first.id;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    header?: string;
    align?: 'start' | 'end';
    width?: string;
  }
}
